import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";
import InputGroup from "../../components/FormComponent/InputGroup";
import logo from "../../assets/images/logo.svg";
import loginBG from "../../assets/images/SignUp.jpg";
import { httprequest } from "../../data/api";
import Button from "../../components/Button";
import useValidator from "../../hooks/useValidator";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import { toast } from "react-toastify";
import { Countries } from "../../utils/DropDown";
import { Icon } from "@iconify/react";

// --| Context files
const SIGNUP_ENDPOINT = "/api/v1/signup";
const SIGNUP_ATTRIBUTION_KEY = "shelf21_signup_attribution_first_touch";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

// Reads first-touch attribution persisted between marketing -> signup navigation.
function readStoredSignupAttribution() {
  try {
    const raw = sessionStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);

    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}

function readSignupAttributionFromSearch(search = "") {
  const params = new URLSearchParams(search);
  const fromUrl = {};

  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v != null && String(v).trim() !== "") fromUrl[k] = String(v).trim();
  });

  return fromUrl;
}

function mergeSignupAttributionFromLocation(location) {
  if (typeof sessionStorage === "undefined" || typeof window === "undefined") return;

  const fromUrl = readSignupAttributionFromSearch(location.search);

  // Preserve previously captured values, but allow explicit URL params to override.
  const prev = readStoredSignupAttribution() || {};
  const merged = { ...prev, ...fromUrl };

  const hasUtm = UTM_KEYS.some(
    (k) => merged[k] && String(merged[k]).trim()
  );

  if (!merged.referrer && document.referrer && !document.referrer.startsWith(window.location.origin)) {
    merged.referrer = document.referrer.slice(0, 2048);
  }

  merged.captured_page_path = `${location.pathname}${location.search}`.slice(0, 512);

  if (hasUtm || merged.referrer) {
    sessionStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify(merged));
  }
}

function buildSignupAttributionPayloadForApi(location) {
  // Merge latest URL values with stored first-touch data before sending to API.
  const stored = readStoredSignupAttribution() || {};
  const fromUrl = readSignupAttributionFromSearch(location.search);
  const merged = { ...stored, ...fromUrl };

  if (!Object.keys(merged).length) return null;

  const out = {};

  if (!merged.referrer && document.referrer && !document.referrer.startsWith(window.location.origin)) {
    merged.referrer = document.referrer.slice(0, 2048);
  }
  merged.captured_page_path = `${location.pathname}${location.search}`.slice(0, 512);

  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "referrer", "captured_page_path"].forEach(
    (k) => {
      if (merged[k] != null && String(merged[k]).trim() !== "") out[k] = String(merged[k]).trim();
    }
  );

  return Object.keys(out).length ? out : null;
}
// const COUNTRIES_API = "https://countriesnow.space/api/v0.1/countries/iso";
// const COUNTRY_CURRENCIES = {
//   NG: "NGN", // Nigeria
//   US: "USD", // United States
//   GH: "GHS", // Ghana
//   ZA: "ZAR", // South Africa
//   KE: "KES" // Kenya
// };

// const DEFAULT_CURRENCY = "USD";

// const EXCHANGE_RATES = {
//   USD: 1500,  // 1 USD = 1500 NGN
//   GBP: 1900,  // 1 GBP = 1900 NGN
//   EUR: 1600,  // 1 EUR = 1600 NGN
//   GHS: 120,   // 1 GHS = 120 NGN
//   ZAR: 80,    // 1 ZAR = 80 NGN
//   KES: 12     // 1 KES = 12 NGN
// };

const Signup = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isReferralDisabled, setIsReferralDisabled] = useState(false);
  const [signUpForm, setSignupForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    email: "",
    phone: "",
    school_name: "",
    school_address: "",
    country: "",
    useDemo: false,
    useOwnData: false,
    termsAgreement: false,
    referral_code: ""
    // emailUpdates: false,
  });
  const customValidator = {
    password: {
      message:
        "Password should be at least 8 characters, contain a lowercase, uppercase, a number and a special character",
      rule: (val, params, validator) => {
        return validator.helpers.testRegex(
          val,
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[a-zA-Z\d!@#$%^&*(),.?":{}|<>]{8,}$/
        );
      },
      required: true
    },
    termsAgreement: {
      message: "You must agree to the Terms of Service and Privacy Policy",
      rule: (val) => val === true,
      required: true
    }
  };
  const [signupValidator, showValidationMessage] = useValidator(
    {},
    customValidator
  );

  // const getCurrencyForCountry = (countryCode) => {
  //   return COUNTRY_CURRENCIES[countryCode] || DEFAULT_CURRENCY;
  // };

  // const calculatePriceInUserCurrency = (basePriceNGN, userCurrency) => {
  //   const rate = EXCHANGE_RATES[userCurrency] || EXCHANGE_RATES[DEFAULT_CURRENCY];
  //   const convertedPrice = basePriceNGN / rate;

  //   return Math.ceil(convertedPrice);
  // };

  const handleCountryChange = (e) => {
    const selectedIso2 = e.target.value;
    const country = Countries.find((c) => c.Iso2 === selectedIso2);
    setSelectedCountry(country);
    setSignupForm((prev) => ({
      ...prev,
      country: selectedIso2
    }));
  };

  const {
    first_name,
    last_name,
    password,
    email,
    school_name,
    phone,
    country,
    referral_code
  } = signUpForm;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "useDemo" && checked) {
      setSignupForm((prev) => ({
        ...prev,
        useDemo: true,
        useOwnData: false
      }));
    } else if (name === "useOwnData" && checked) {
      setSignupForm((prev) => ({
        ...prev,
        useOwnData: true,
        useDemo: false
      }));
    } else {
      setSignupForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pricing_model_id = searchParams.get("pmid");
    const plan_name = searchParams.get("pname");
    const variantId = searchParams.get("var_id");
    const refCode = searchParams.get("ref");

    if (refCode) {
      setSignupForm((prev) => ({
        ...prev,
        referral_code: refCode
      }));
      setIsReferralDisabled(true);
    }

    if (pricing_model_id) {
      const selectedPricing = {
        pricing_model_id,
        plan_name,
        variantId,
        // base_price_ngn: base_price_ngn ? parseInt(base_price_ngn) : null,
        callback_url: `${process.env.REACT_APP_BASE_URL}/auth/login`
      };
      localStorage.setItem("selectedPricing", JSON.stringify(selectedPricing));
    }

    mergeSignupAttributionFromLocation(location);
  }, [location.pathname, location.search]);

  const handleSignup = (e) => {
    e.preventDefault();
    mergeSignupAttributionFromLocation(location);

    let formattedPhone = signUpForm.phone;
    if (formattedPhone.startsWith("0")) {
      formattedPhone = formattedPhone.substring(1); // Remove the first character (0)
    }
    if (!signUpForm.useDemo && !signUpForm.useOwnData) {
      toast.error("You must choose either Demo Data or Own Data");

      return;
    }

    const phoneWithCode = selectedCountry
      ? `+${selectedCountry.callingCode}${formattedPhone}`
      : formattedPhone;

    const selectedPricing = localStorage.getItem("selectedPricing");
    let subscription = null;

    if (selectedPricing) {
      const pricingInfo = JSON.parse(selectedPricing);
      // const userCurrency = country ? getCurrencyForCountry(country) : DEFAULT_CURRENCY;

      // const calculatedPrice = calculatePriceInUserCurrency(pricingInfo.base_price_ngn, userCurrency);

      subscription = {
        plan_id: pricingInfo.pricing_model_id,
        pricing_plan_name: pricingInfo.plan_name,
        callback_url: pricingInfo.callback_url
        // currency: userCurrency,
      };

      if (pricingInfo.variantId) {
        subscription.plan_variant_id = pricingInfo.variantId;
      }
      localStorage.removeItem("selectedPricing");
    }

    const payload = {
      ...signUpForm,
      phone: phoneWithCode,
      populate_demo_data: signUpForm.useDemo && !signUpForm.useOwnData
    };

    delete payload.useDemo;
    delete payload.useOwnData;
    delete payload.termsAgreement;

    if (!payload.referral_code) {
      delete payload.referral_code;
    }

    if (subscription) {
      payload.subscription = subscription;
    }

    // Attach attribution only when at least one meaningful field is present.
    const signupAttributionPayload = buildSignupAttributionPayloadForApi(location);
    if (signupAttributionPayload) {
      payload.signup_attribution = signupAttributionPayload;
    }

    if (signupValidator.allValid()) {
      setIsLoading(true);
      httprequest
        .post(SIGNUP_ENDPOINT, payload)
        .then((res) => {
          setIsLoading(false);
          toast.success("Account Created Successfully");

          try {
            sessionStorage.removeItem(SIGNUP_ATTRIBUTION_KEY);
          } catch {
            /* non-fatal */
          }

          const paymentDetails = res?.data?.payment_details;
          if (paymentDetails?.redirect_url) {
            window.location.href = paymentDetails.redirect_url;
          } else {
            setTimeout(() => navigate("/auth/login"), 1000);
          }
        })

        .catch((err) => {
          setIsLoading(false);
          toast.error(err?.response?.data?.message);
        });
    } else {
      showValidationMessage(true);
    }
  };
  const [viewSecretKeys, setViewSecretKeys] = useState({
    secret_key: false
  });

  const toggleSecretKey = (key) => {
    setViewSecretKeys((prevState) => ({
      ...prevState,
      [key]: !prevState[key]
    }));
  };

  const showDemoInfo = signUpForm.useDemo && !signUpForm.useOwnData;
  const showOwnInfo = signUpForm.useOwnData && !signUpForm.useDemo;

  // useEffect(() => {
  //   const fetchCountries = async () => {
  //     try {
  //       const response = await axios.get(COUNTRIES_API);
  //       const countryList = response.data.data.map((item) => ({
  //         value: item.Iso2,
  //         label: item.name
  //       }));
  //       setCountries(countryList);
  //     } catch (error) {
  //       toast.error("Failed to fetch countries. Please try again.");
  //     }
  //   };
  //   fetchCountries();
  // }, []);

  return (
    <div className="auth-wrapper">
      <div className="auth-container">
        <div className="auth-flex">
          <div className="auth-bg-container">
            <img src={loginBG} className="auth-bg-img" alt="login BG" />
          </div>
          <div className="auth-form-container">
            <div className="auth-logo-container">
              <img src={logo} alt="logo" className="auth-logo" />
            </div>
            <div className="auth-form">
              <React.Fragment>
                <div className="auth-heading black900">Signup</div>
                <div className="auth-subheading black900">
                  Create your Shelf21 account by entering your email and password to get started.
                </div>
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <InputGroup
                      label="First Name"
                      inputValue={first_name}
                      onChange={handleChange}
                      margin="normal"
                      inputName="first_name"
                      inputType="text"
                      required
                    />
                    {signupValidator.message(
                      "first_name",
                      first_name,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <InputGroup
                      label="Last Name"
                      inputValue={last_name}
                      onChange={handleChange}
                      margin="normal"
                      inputName="last_name"
                      inputType="text"
                      required
                    />
                    {signupValidator.message(
                      "last_name",
                      last_name,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Email Address"
                      inputValue={email}
                      onChange={handleChange}
                      margin="normal"
                      inputName="email"
                      inputType="email"
                      required
                    />
                    {signupValidator.message("email", email, "required")}
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Country"
                      required
                      children={
                        <select
                          onChange={handleCountryChange}
                          name="country"
                          defaultValue={country}
                        >
                          <option>---Select Country---</option>
                          {Countries.map((country, i) => {
                            return (
                              <option key={i} value={country.Iso2}>
                                {country.name}
                              </option>
                            );
                          })}
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <div className="phone-input-container">
                      <div className="phone-prefix">
                        {selectedCountry ? `${selectedCountry.callingCode}` : null}
                      </div>

                      <InputGroup
                        label="Phone Number"
                        inputValue={phone}
                        required
                        onChange={handleChange}
                        margin="normal"
                        inputName="phone"
                        inputType="tel"
                        placeholder="Enter Phone Number"
                      />
                    </div>
                    {signupValidator.message("phone_number", phone, "phone")}
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="School Name"
                      inputValue={school_name}
                      onChange={handleChange}
                      margin="normal"
                      required
                      inputName="school_name"
                      inputType="text"
                    />
                    {signupValidator.message(
                      "school_name",
                      school_name,
                      "required"
                    )}
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="School Address"
                      inputValue={signUpForm.school_address}
                      onChange={handleChange}
                      margin="normal"
                      inputName="school_address"
                      inputType="text"
                      placeholder="Enter School Address"
                      required
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Referral Code (Optional)"
                      value={referral_code}
                      onChange={handleChange}
                      margin="normal"
                      inputName="referral_code"
                      inputType="text"
                      placeHolder="Enter Referral Code"
                      disabled={isReferralDisabled}
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Password"
                      required
                      inputValue={password}
                      onChange={handleChange}
                      inputName="password"
                      inputType={
                        viewSecretKeys.secret_key ? "text" : "password"
                      }
                    />
                    <div
                      className="passwords-eye"
                      onClick={() => toggleSecretKey("secret_key")}
                    >
                      {viewSecretKeys.secret_key ? (
                        <Icon icon="fluent:eye-16-regular" color="black" />
                      ) : (
                        <Icon icon="iconamoon:eye-off-light" color=" black" />
                      )}
                    </div>
                    <div style={{ marginTop: "-17px" }}>
                      {signupValidator.message(
                        "password",
                        password,
                        "required|password"
                      )}
                    </div>
                  </Grid>
                  <Grid className="data-selection-container">
                    <div className="data-selection-options">
                      <label className="checkbox_label">
                        <input
                          type="checkbox"
                          name="useDemo"
                          checked={signUpForm.useDemo}
                          onChange={handleChange}
                          className="checkbox-input"
                        />
                        <span>Start with Demo Data</span>
                      </label>
                      <label className="checkbox_label">
                        <input
                          type="checkbox"
                          name="useOwnData"
                          checked={signUpForm.useOwnData}
                          onChange={handleChange}
                          className="checkbox-input"
                        />
                        <span>Use Your Own Data</span>
                      </label>
                    </div>
                    {showDemoInfo && (
                      <div className="info-text">
                        <span className="info-icon">ⓘ</span>
                        <p className="info-message demo">
                          Your account will contain a demo list of people and
                          other data to try Shelf21 with. Note that this data
                          will be cleared after 14 days.
                        </p>
                      </div>
                    )}

                    {showOwnInfo && (
                      <div className="info-text">
                        <span className="info-icon">ⓘ</span>
                        <p className="info-message own">
                          Your account will be empty, so you can add your own
                          data.
                        </p>
                      </div>
                    )}
                  </Grid>

                  <Grid className="terms-container">
                    <label className=" checkbox-block">
                      <input
                        type="checkbox"
                        // name="emailUpdates"
                        // onChange={handleChange}
                        className="checkbox-input"
                      />
                      <span>
                        Email me about product updates, and other offers. You
                        can unsubscribe anytime
                      </span>
                    </label>

                    <label className=" checkbox-block">
                      <input
                        type="checkbox"
                        name="termsAgreement"
                        checked={signUpForm.termsAgreement}
                        onChange={handleChange}
                        className="checkbox-input"
                        required
                      />
                      <span>
                        I agree to the{" "}
                        <a
                          href="https://www.shelf21.com/termsofservice"
                          className="app-primary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Services
                        </a>{" "}
                        and I confirm I have read the{" "}
                        <a
                          href="https://www.shelf21.com/privacy-policy"
                          className="app-primary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>
                        .
                      </span>
                    </label>
                    <p style={{ marginTop: "-5px", marginBottom: "0" }} className="validator-error">
                      {signupValidator.message("termsAgreement", signUpForm.termsAgreement, "required|termsAgreement")}
                    </p>
                  </Grid>

                  {/* <Grid item lg={6} md={6} sm={6} xs={12}>
                    <InputGroup
                      label="School Address"
                      inputValue={school_address}
                      onChange={handleChange}
                      margin="normal"
                      inputName="school_address"
                      inputType="text"
                    />
                    {signupValidator.message("school_address", school_address, "required")}
                  </Grid> */}
                </Grid>
                <div className="auth-form-buttons">
                  <Button
                    buttonName="Create an Account"
                    variant="primary"
                    buttonSize="full"
                    onClick={handleSignup}
                    isLoading={isLoading}
                    color="btndefault"
                  />
                </div>
              </React.Fragment>
              <div className="auth-divider">
                <hr />
                <span className="brown900">Or</span>
              </div>
              <div className="brown900 auth-account-button">
                Already have an account ?
                <Link to="/auth/login" className="app-primary">
                  Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <ToastContainer /> */}
    </div>
  );
};

export default Signup;
